import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
    useGetAllAdminsQuery,
    useCreateAdminMutation,
    useUpdateAdminMutation,
    useDeleteAdminMutation,
    useGetRoleConfigQuery,
    useUpdateAdminRbacMutation,
    useUpdateAdminStatusMutation,
    useGetRoleDefaultsQuery,
    Admin,
    CreateAdminRequest,
    UpdateAdminRequest
} from '../../store/slices/apiSlice';
import {
    Users,
    Shield,
    Plus,
    Edit,
    Trash2,
    Search,
    Mail,
    Phone,
    Calendar,
    Eye,
    Filter,
    Download,
    MoreHorizontal,
    CheckCircle,
    XCircle,
    AlertTriangle,
    UserPlus,
    Settings,
    RefreshCw,
    ChevronDown,
    ChevronUp,
    Star,
    Clock,
    Activity,
    Globe,
    Lock,
    Unlock,
    EyeOff,
    Eye as EyeIcon,
    X
} from 'lucide-react';
import toast from 'react-hot-toast';
import UserAvatar from '../../components/common/UserAvatar';

type AdminRole = 'admin' | 'manager' | 'exclusive';

const ROLE_DEFAULTS: Record<AdminRole, Record<string, boolean>> = {
    admin: {
        view_dashboard: true,
        view_users: true,
        manage_users: true,
        view_dealers: true,
        manage_dealers: true,
        view_projects: true,
        manage_projects: true,
        view_investments: true,
        manage_investments: true,
        manage_categories: true,
        view_marketplace: true,
        manage_marketplace: true,
        manage_marketplace_settings: true,
        view_inquiries: true,
        manage_inquiries: true,
        view_transactions: true,
        manage_transactions: true,
        view_kyc: true,
        manage_kyc: true,
        manage_bank_accounts: true,
        manage_wallet: true,
        manage_payment_methods: true,
        manage_child_sponsorship: true,
        manage_apartments: true,
        manage_bookings: true,
        manage_memberships: true,
        manage_solar_leads: true,
        manage_blogs: true,
        manage_testimonials: true,
        manage_cta_popups: true,
        manage_jobs: true,
        view_contact_submissions: true,
        manage_notifications: true,
        manage_admins: true
    },
    manager: {
        view_dashboard: true,
        view_users: true,
        manage_users: true,
        view_dealers: true,
        manage_dealers: true,
        view_projects: true,
        manage_projects: true,
        view_investments: true,
        manage_investments: true,
        manage_categories: true,
        view_marketplace: true,
        manage_marketplace: true,
        manage_marketplace_settings: true,
        view_inquiries: true,
        manage_inquiries: true,
        view_transactions: true,
        manage_transactions: true,
        view_kyc: true,
        manage_kyc: true,
        manage_bank_accounts: false,
        manage_wallet: false,
        manage_payment_methods: false,
        manage_child_sponsorship: true,
        manage_apartments: true,
        manage_bookings: true,
        manage_memberships: true,
        manage_solar_leads: true,
        manage_blogs: true,
        manage_testimonials: true,
        manage_cta_popups: true,
        manage_jobs: true,
        view_contact_submissions: true,
        manage_notifications: true,
        manage_admins: false
    },
    exclusive: {
        view_dashboard: true,
        view_users: true,
        manage_users: false,
        view_dealers: true,
        manage_dealers: false,
        view_projects: true,
        manage_projects: false,
        view_investments: true,
        manage_investments: false,
        manage_categories: false,
        view_marketplace: true,
        manage_marketplace: false,
        manage_marketplace_settings: false,
        view_inquiries: true,
        manage_inquiries: false,
        view_transactions: true,
        manage_transactions: false,
        view_kyc: true,
        manage_kyc: false,
        manage_bank_accounts: false,
        manage_wallet: false,
        manage_payment_methods: false,
        manage_child_sponsorship: false,
        manage_apartments: false,
        manage_bookings: false,
        manage_memberships: false,
        manage_solar_leads: false,
        manage_blogs: false,
        manage_testimonials: false,
        manage_cta_popups: false,
        manage_jobs: false,
        view_contact_submissions: true,
        manage_notifications: false,
        manage_admins: false
    }
};

const AdminUsers: React.FC = () => {
    // Redux selectors
    const { user: loggedInUser } = useSelector((state: RootState) => state.auth);
    const loggedInAdminId = loggedInUser?.admin_id || loggedInUser?.id;

    // Tabs state
    const [activeTab, setActiveTab] = useState<'roles' | 'permissions'>('roles');

    // Modals state
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);

    // Search and Sort state (Roles tab)
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [showPassword, setShowPassword] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [formData, setFormData] = useState<CreateAdminRequest>({
        name: '',
        email: '',
        password: '',
        mobile_number: '',
        role: 'exclusive',
    });

    // Permissions tab state
    const [permissionSelectedAdminId, setPermissionSelectedAdminId] = useState<number | null>(null);
    const [permissionSearchTerm, setPermissionSearchTerm] = useState('');

    // API hooks
    const { data: adminsData, isLoading, error, refetch } = useGetAllAdminsQuery();
    const { data: configData, isLoading: isLoadingConfig } = useGetRoleConfigQuery();
    const [createAdmin, { isLoading: isCreating }] = useCreateAdminMutation();
    const [updateAdmin, { isLoading: isUpdating }] = useUpdateAdminMutation();
    const [deleteAdmin, { isLoading: isDeleting }] = useDeleteAdminMutation();
    const [updateAdminRbac, { isLoading: isUpdatingRbac }] = useUpdateAdminRbacMutation();
    const [updateAdminStatus, { isLoading: isUpdatingStatus }] = useUpdateAdminStatusMutation();

    // Fetch dynamic role defaults from the backend API
    const { data: adminDefaultsRes } = useGetRoleDefaultsQuery('admin');
    const { data: managerDefaultsRes } = useGetRoleDefaultsQuery('manager');
    const { data: exclusiveDefaultsRes } = useGetRoleDefaultsQuery('exclusive');

    const dynamicRoleDefaults = {
        admin: adminDefaultsRes?.data || ROLE_DEFAULTS.admin,
        manager: managerDefaultsRes?.data || ROLE_DEFAULTS.manager,
        exclusive: exclusiveDefaultsRes?.data || ROLE_DEFAULTS.exclusive,
    };

    const admins = adminsData?.data || [];

    // Auto-select first admin on Permissions tab when data loads
    useEffect(() => {
        if (admins.length > 0 && !permissionSelectedAdminId) {
            setPermissionSelectedAdminId(admins[0].admin_id);
        }
    }, [admins, permissionSelectedAdminId]);

    // Selected admin for permissions editing
    const selectedAdminData = admins.find(a => a.admin_id === permissionSelectedAdminId);
    const currentPermissions = selectedAdminData?.permissions || {};
    const roleDefaults = selectedAdminData ? (dynamicRoleDefaults[selectedAdminData.role] || dynamicRoleDefaults.exclusive) : {};

    // Calculate active overrides count
    const overrideCount = selectedAdminData
        ? Object.keys(currentPermissions).reduce((count, key) => {
              return currentPermissions[key] !== roleDefaults[key] ? count + 1 : count;
          }, 0)
        : 0;

    // Filter and sort admins (Roles tab)
    const filteredAndSortedAdmins = admins
        .filter(admin => {
            const matchesSearch =
                admin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                admin.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                admin.mobile_number.includes(searchTerm);

            return matchesSearch;
        })
        .sort((a, b) => {
            let aValue = a[sortBy as keyof Admin];
            let bValue = b[sortBy as keyof Admin];

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (sortOrder === 'asc') {
                return (aValue || '') > (bValue || '') ? 1 : -1;
            } else {
                return (aValue || '') < (bValue || '') ? 1 : -1;
            }
        });

    // Pagination logic
    const totalPages = Math.ceil(filteredAndSortedAdmins.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedAdmins = filteredAndSortedAdmins.slice(startIndex, startIndex + itemsPerPage);

    // Form submission handlers
    const handleCreateSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await createAdmin(formData).unwrap();
            toast.success('Admin created successfully!');
            setIsCreateModalOpen(false);
            setFormData({
                name: '',
                email: '',
                password: '',
                mobile_number: '',
                role: 'exclusive',
            });
        } catch (error) {
            console.error('Failed to create admin:', error);
            toast.error('Failed to create admin. Please try again.');
        }
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedAdmin) return;

        try {
            const updateData: UpdateAdminRequest = {
                admin_id: selectedAdmin.admin_id,
                name: formData.name,
                mobile_number: formData.mobile_number,
                password: formData.password,
                role: formData.role,
            };
            await updateAdmin(updateData).unwrap();
            toast.success('Admin updated successfully!');
            setIsEditModalOpen(false);
            setSelectedAdmin(null);
            setFormData({
                name: '',
                email: '',
                password: '',
                mobile_number: '',
                role: 'exclusive',
            });
        } catch (error) {
            console.error('Failed to update admin:', error);
            toast.error('Failed to update admin. Please try again.');
        }
    };

    const handleDeleteAdmin = async () => {
        if (!selectedAdmin) return;

        try {
            await deleteAdmin({ admin_id: selectedAdmin.admin_id }).unwrap();
            toast.success('Admin deleted successfully!');
            setIsDeleteModalOpen(false);
            setSelectedAdmin(null);
        } catch (error) {
            console.error('Failed to delete admin:', error);
            toast.error('Failed to delete admin. Please try again.');
        }
    };

    // RBAC Modification Handlers
    const handleStatusToggle = async (adminId: number, isActive: boolean) => {
        if (adminId === Number(loggedInAdminId)) {
            toast.error("You cannot deactivate your own account.");
            return;
        }
        try {
            await updateAdminStatus({ admin_id: adminId, is_active: isActive }).unwrap();
            toast.success(`Admin status updated to ${isActive ? 'Active' : 'Inactive'} successfully.`);
        } catch (error) {
            console.error('Failed to update admin status:', error);
            toast.error('Failed to update admin status.');
        }
    };

    const handleRoleChange = async (adminId: number, newRole: AdminRole, currentPermissionsMap: Record<string, boolean>) => {
        const admin = admins.find(a => a.admin_id === adminId);
        if (!admin) return;

        // Compute current overrides based on defaults of OLD role
        const oldDefaults = dynamicRoleDefaults[admin.role] || dynamicRoleDefaults.exclusive;
        const currentOverrides: Record<string, boolean> = {};
        Object.keys(currentPermissionsMap).forEach(key => {
            if (currentPermissionsMap[key] !== oldDefaults[key]) {
                currentOverrides[key] = currentPermissionsMap[key];
            }
        });

        try {
            await updateAdminRbac({
                admin_id: adminId,
                role: newRole,
                permissions: currentOverrides
            }).unwrap();
            toast.success(`Role updated to ${newRole.toUpperCase()} successfully.`);
        } catch (error) {
            console.error('Failed to update admin role/RBAC:', error);
            toast.error('Failed to update admin role.');
        }
    };

    const handleTogglePermission = async (key: string) => {
        if (!selectedAdminData) return;

        // Toggle permission value
        const updatedPermissions = {
            ...currentPermissions,
            [key]: !currentPermissions[key]
        };

        // Compute overrides relative to current role defaults
        const newOverrides: Record<string, boolean> = {};
        Object.keys(updatedPermissions).forEach(k => {
            if (updatedPermissions[k] !== roleDefaults[k]) {
                newOverrides[k] = updatedPermissions[k];
            }
        });

        try {
            await updateAdminRbac({
                admin_id: selectedAdminData.admin_id,
                role: selectedAdminData.role,
                permissions: newOverrides
            }).unwrap();
            toast.success('Permission updated successfully.');
        } catch (error) {
            console.error('Failed to toggle permission:', error);
            toast.error('Failed to toggle permission.');
        }
    };

    const handleToggleModuleAll = async (moduleName: string, value: boolean) => {
        if (!selectedAdminData) return;

        const moduleGroup = configData?.data.find(m => m.module === moduleName);
        if (!moduleGroup) return;

        const updatedPermissions = { ...currentPermissions };
        moduleGroup.permissions.forEach(p => {
            updatedPermissions[p.key] = value;
        });

        const newOverrides: Record<string, boolean> = {};
        Object.keys(updatedPermissions).forEach(k => {
            if (updatedPermissions[k] !== roleDefaults[k]) {
                newOverrides[k] = updatedPermissions[k];
            }
        });

        try {
            await updateAdminRbac({
                admin_id: selectedAdminData.admin_id,
                role: selectedAdminData.role,
                permissions: newOverrides
            }).unwrap();
            toast.success(`All permissions in ${moduleName} set to ${value ? 'Active' : 'Inactive'}.`);
        } catch (error) {
            console.error('Failed to update module permissions:', error);
            toast.error('Failed to update permissions.');
        }
    };

    const handleClearOverrides = async () => {
        if (!selectedAdminData) return;

        try {
            await updateAdminRbac({
                admin_id: selectedAdminData.admin_id,
                role: selectedAdminData.role,
                permissions: {}
            }).unwrap();
            toast.success('All permission overrides cleared successfully.');
        } catch (error) {
            console.error('Failed to clear overrides:', error);
            toast.error('Failed to clear overrides.');
        }
    };

    const openEditModal = (admin: Admin) => {
        setSelectedAdmin(admin);
        setFormData({
            name: admin.name,
            email: admin.email,
            password: '',
            mobile_number: admin.mobile_number,
            role: admin.role || 'exclusive',
        });
        setIsEditModalOpen(true);
    };

    const openDeleteModal = (admin: Admin) => {
        setSelectedAdmin(admin);
        setIsDeleteModalOpen(true);
    };

    const openViewModal = (admin: Admin) => {
        setSelectedAdmin(admin);
        setIsViewModalOpen(true);
    };

    const formatDate = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);

    const formatLastLogin = (dateString: string | null) => {
        if (!dateString) return 'Never logged in';
        return new Date(dateString).toLocaleString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: true
        });
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading admin data...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                        <p className="text-red-600 text-lg font-medium">Error loading admins</p>
                        <p className="text-gray-600 mt-2">Please try refreshing the page</p>
                    </div>
                </div>
            </div>
        );
    }

    // Filter permissions for permissions search
    const filteredConfig = configData?.data.map(moduleGroup => {
        const matchingPermissions = moduleGroup.permissions.filter(perm =>
            perm.label.toLowerCase().includes(permissionSearchTerm.toLowerCase()) ||
            perm.key.toLowerCase().includes(permissionSearchTerm.toLowerCase())
        );
        return {
            ...moduleGroup,
            permissions: matchingPermissions
        };
    }).filter(moduleGroup => moduleGroup.permissions.length > 0) || [];

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
            <div className="space-y-6">
                {/* Header Section */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                                    <Shield className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Admin Management</h1>
                                    <p className="text-gray-600 font-medium text-sm">Manage admin accounts, base roles, and custom overrides</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => refetch()}
                                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all duration-200"
                                title="Refresh data"
                            >
                                <RefreshCw className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Tabs Switcher */}
                <div className="flex gap-3 mb-6 bg-white/40 p-1.5 rounded-2xl border border-gray-200/50 backdrop-blur-sm inline-flex">
                    <button
                        onClick={() => setActiveTab('roles')}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 ${
                            activeTab === 'roles'
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-white/60'
                        }`}
                    >
                        <Users className="w-4 h-4" />
                        Roles
                    </button>
                    <button
                        onClick={() => setActiveTab('permissions')}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 ${
                            activeTab === 'permissions'
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-white/60'
                        }`}
                    >
                        <Lock className="w-4 h-4" />
                        Permission
                    </button>
                </div>

                {activeTab === 'roles' ? (
                    <>
                        {/* Search and Filters */}
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                            <div className="flex flex-col lg:flex-row gap-4">
                                <div className="flex-1 relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search admins by name, email, or phone..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    />
                                </div>

                                <div className="flex items-center gap-3">
                                    <select
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white text-sm"
                                    >
                                        <option value="name">Sort by Name</option>
                                        <option value="email">Sort by Email</option>
                                        <option value="created_at">Sort by Date</option>
                                    </select>

                                    <button
                                        onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                                        className="p-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all duration-200"
                                    >
                                        {sortOrder === 'asc' ? <ChevronUp className="w-5 h-5 text-gray-600" /> : <ChevronDown className="w-5 h-5 text-gray-600" />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* System Administrators Table Card */}
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-900">System Administrators</h2>
                                <button
                                    onClick={() => setIsCreateModalOpen(true)}
                                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5"
                                >
                                    <Plus className="w-4.5 h-4.5" />
                                    <span className="font-semibold text-sm">Add New Admin</span>
                                </button>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50/70 border-b border-gray-100">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Admin</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Email</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Role</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Last Login</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-100">
                                        {paginatedAdmins.map((admin) => {
                                            const isMe = admin.admin_id === Number(loggedInAdminId);
                                            return (
                                                <tr key={admin.admin_id} className="hover:bg-gray-50/50 transition-colors duration-200">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center space-x-3">
                                                            <UserAvatar 
                                                                src={admin.profile_image} 
                                                                name={admin.name} 
                                                                size="sm" 
                                                            />
                                                            <div>
                                                                <div className="font-semibold text-gray-900 text-sm flex items-center gap-1.5">
                                                                    {admin.name}
                                                                </div>
                                                                <div className="text-xs text-gray-400">ID: {admin.admin_id}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-700 font-medium">
                                                        {admin.email}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="relative inline-block">
                                                            <select
                                                                value={admin.role}
                                                                disabled={isUpdatingRbac || admin.role === 'admin'}
                                                                onChange={(e) => handleRoleChange(admin.admin_id, e.target.value as AdminRole, admin.permissions || {})}
                                                                className={`appearance-none pl-3 pr-8 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-all duration-200 focus:outline-none ${
                                                                    admin.role === 'admin'
                                                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200 cursor-not-allowed'
                                                                        : admin.role === 'manager'
                                                                        ? 'bg-indigo-50 text-indigo-700 border-indigo-200 cursor-pointer'
                                                                        : 'bg-purple-50 text-purple-700 border-purple-200 cursor-pointer'
                                                                }`}
                                                            >
                                                                <option value="admin">Admin</option>
                                                                <option value="manager">Manager</option>
                                                                <option value="exclusive">Executive</option>
                                                            </select>
                                                            {admin.role !== 'admin' && (
                                                                <ChevronDown className="w-3 h-3 absolute right-2.5 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500" />
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        {isMe ? (
                                                            <span className="text-emerald-600 font-bold text-xs uppercase tracking-wider">Active</span>
                                                        ) : (
                                                            <button
                                                                onClick={() => handleStatusToggle(admin.admin_id, !admin.is_active)}
                                                                disabled={isUpdatingStatus}
                                                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                                                    admin.is_active ? 'bg-emerald-500' : 'bg-gray-200'
                                                                } ${isUpdatingStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                            >
                                                                <span
                                                                    aria-hidden="true"
                                                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                                        admin.is_active ? 'translate-x-5' : 'translate-x-0'
                                                                    }`}
                                                                />
                                                            </button>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-xs font-semibold text-gray-500">
                                                        {formatLastLogin(admin.last_login)}
                                                    </td>
                                                    <td className="px-6 py-4 text-xs">
                                                        <div className="flex items-center space-x-3">
                                                            {isMe && (
                                                                <span className="px-2 py-0.5 text-[9px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md uppercase tracking-wider">YOU</span>
                                                            )}
                                                            <button
                                                                onClick={() => openEditModal(admin)}
                                                                className="text-gray-500 hover:text-indigo-600 font-semibold transition-colors duration-200"
                                                                title="Edit details"
                                                            >
                                                                Edit
                                                            </button>
                                                            <button
                                                                onClick={() => openDeleteModal(admin)}
                                                                disabled={isMe || isDeleting}
                                                                className={`font-semibold transition-colors duration-200 ${isMe ? 'text-gray-300 cursor-not-allowed' : 'text-red-500 hover:text-red-600'}`}
                                                                title={isMe ? "Cannot delete yourself" : "Delete admin"}
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                        disabled={currentPage === 1}
                                        className="flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                        Previous
                                    </button>

                                    <div className="flex items-center gap-1">
                                        {Array.from({ length: Math.min(4, totalPages) }, (_, i) => {
                                            let pageNum: number;
                                            let showEllipsis = false;

                                            if (totalPages <= 4) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 2) {
                                                pageNum = i + 1;
                                                showEllipsis = i === 3 && totalPages > 4;
                                            } else if (currentPage >= totalPages - 1) {
                                                pageNum = totalPages - 3 + i;
                                                showEllipsis = i === 0 && totalPages > 4;
                                            } else {
                                                pageNum = currentPage - 1 + i;
                                                showEllipsis = (i === 0 && currentPage > 3) || (i === 3 && currentPage < totalPages - 2);
                                            }

                                            return (
                                                <React.Fragment key={pageNum}>
                                                    {showEllipsis && (
                                                        <span className="px-2 text-gray-500">...</span>
                                                    )}
                                                    <button
                                                        onClick={() => setCurrentPage(pageNum)}
                                                        className={`w-8 h-8 p-0 text-sm border rounded-md ${currentPage === pageNum
                                                            ? 'bg-blue-600 text-white border-blue-600'
                                                            : 'border-gray-300 hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>

                                    <button
                                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                        disabled={currentPage === totalPages}
                                        className="flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    /* Permissions Tab Content */
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 space-y-6">
                        {/* Selection & Base Info Header */}
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-gray-100">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                    <Lock className="w-5 h-5 text-blue-600" />
                                    Role-Based Permissions
                                </h2>
                                <p className="text-sm text-gray-500">Configure role and granular privileges for each administrator.</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-semibold text-gray-700">Select Admin:</label>
                                <select
                                    value={permissionSelectedAdminId || ''}
                                    onChange={(e) => setPermissionSelectedAdminId(Number(e.target.value))}
                                    className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm font-medium text-gray-800"
                                >
                                    <option value="">Select an Administrator</option>
                                    {admins.map(admin => (
                                        <option key={admin.admin_id} value={admin.admin_id}>
                                            {admin.name} ({admin.role.toUpperCase()})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {selectedAdminData ? (
                            <>
                                {selectedAdminData.role === 'admin' && (
                                    <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-4 flex items-start gap-3 mb-6">
                                        <Shield className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5 animate-pulse" />
                                        <div>
                                            <h4 className="text-sm font-bold text-indigo-800">Admin Permissions Locked</h4>
                                            <p className="text-xs text-indigo-600 mt-1 font-semibold leading-relaxed">
                                                Administrators have permanent full access to all system modules. Their roles and permissions are locked and cannot be overridden.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Access Level, Override Count, Search & Clear Overrides Grid */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-2">
                                    {/* Access Level Card */}
                                    <div className="bg-gray-50/50 rounded-2xl p-5 border border-gray-100 flex flex-col justify-between">
                                        <div>
                                            <div className="flex items-center gap-2 mb-3">
                                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Access Level</span>
                                                <span className="px-2 py-0.5 text-[9px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-full uppercase tracking-wider">BASE ROLE</span>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {(['admin', 'manager', 'exclusive'] as AdminRole[]).map((roleKey) => {
                                                    const isActive = selectedAdminData.role === roleKey;
                                                    const label = roleKey === 'exclusive' ? 'EXECUTIVE' : roleKey.toUpperCase();
                                                    const isLocked = selectedAdminData.role === 'admin';
                                                    return (
                                                        <button
                                                            key={roleKey}
                                                            onClick={() => handleRoleChange(selectedAdminData.admin_id, roleKey, selectedAdminData.permissions || {})}
                                                            disabled={isUpdatingRbac || isLocked}
                                                            className={`px-4 py-2 rounded-xl text-xs font-bold tracking-wider transition-all duration-300 border ${
                                                                isActive
                                                                    ? 'bg-blue-600 text-white border-transparent shadow-md'
                                                                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                                            } ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                        >
                                                            {label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <p className="text-[11px] text-gray-500 mt-4 leading-relaxed">
                                            Selecting a role resets the base permission matrix. Current active overrides ({overrideCount}) are preserved until manually reset.
                                        </p>
                                    </div>

                                    {/* Search & Actions Card */}
                                    <div className="flex flex-col justify-between gap-4">
                                        <div className="relative">
                                            <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder="Search permissions..."
                                                value={permissionSearchTerm}
                                                onChange={(e) => setPermissionSearchTerm(e.target.value)}
                                                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                                            />
                                        </div>
                                        <button
                                            onClick={handleClearOverrides}
                                            disabled={overrideCount === 0 || isUpdatingRbac || selectedAdminData.role === 'admin'}
                                            className="flex items-center justify-center gap-2 w-full py-3 border border-gray-200 hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200 text-gray-600 bg-white rounded-xl text-sm font-semibold transition-all duration-300 shadow-sm disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-gray-600 disabled:hover:border-gray-200"
                                        >
                                            <RefreshCw className={`w-4 h-4 ${isUpdatingRbac ? 'animate-spin' : ''}`} />
                                            Clear All Overrides
                                        </button>
                                    </div>
                                </div>

                                {/* Permissions Modules List */}
                                <div className="space-y-6">
                                    {isLoadingConfig ? (
                                        <div className="py-12 text-center text-gray-500 font-semibold text-sm">
                                            <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                                            Loading permissions configuration...
                                        </div>
                                    ) : filteredConfig.length === 0 ? (
                                        <div className="py-12 text-center text-gray-500 font-semibold text-sm border border-dashed border-gray-200 rounded-2xl">
                                            No permissions match your search term.
                                        </div>
                                    ) : (
                                        filteredConfig.map((moduleGroup) => {
                                            const totalItems = moduleGroup.permissions.length;
                                            return (
                                                <div key={moduleGroup.module} className="border border-gray-150 rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
                                                    {/* Section Header */}
                                                    <div className="bg-gray-50/50 px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
                                                        <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
                                                            {moduleGroup.module}
                                                            <span className="text-[10px] text-gray-400 font-medium">({totalItems} items)</span>
                                                        </h3>
                                                        <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-wider">
                                                            <button
                                                                onClick={() => handleToggleModuleAll(moduleGroup.module, true)}
                                                                disabled={isUpdatingRbac || selectedAdminData.role === 'admin'}
                                                                className="text-blue-600 hover:text-blue-700 transition-colors disabled:opacity-55 disabled:cursor-not-allowed"
                                                            >
                                                                All
                                                            </button>
                                                            <span className="text-gray-300">|</span>
                                                            <button
                                                                onClick={() => handleToggleModuleAll(moduleGroup.module, false)}
                                                                disabled={isUpdatingRbac || selectedAdminData.role === 'admin'}
                                                                className="text-blue-600 hover:text-blue-700 transition-colors disabled:opacity-55 disabled:cursor-not-allowed"
                                                            >
                                                                None
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Section Body - Grid of Permissions */}
                                                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                                                        {moduleGroup.permissions.map((perm) => {
                                                            const isToggledOn = !!currentPermissions[perm.key];
                                                            const isOverride = currentPermissions[perm.key] !== roleDefaults[perm.key];

                                                            return (
                                                                <div key={perm.key} className="flex items-center justify-between p-3 rounded-xl border border-transparent hover:border-gray-100 hover:bg-gray-50/30 transition-all duration-200">
                                                                    <div className="pr-4 space-y-0.5">
                                                                        <span className="text-sm font-semibold text-gray-800 block">
                                                                            {perm.label}
                                                                        </span>
                                                                        <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider block">
                                                                            {perm.key}
                                                                        </span>
                                                                        {isOverride && (
                                                                            <span className="inline-block text-[9px] font-bold text-blue-600 uppercase tracking-wider">Custom Override</span>
                                                                        )}
                                                                    </div>
                                                                    <button
                                                                        onClick={() => handleTogglePermission(perm.key)}
                                                                        disabled={isUpdatingRbac || selectedAdminData.role === 'admin'}
                                                                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                                                            isToggledOn ? 'bg-blue-600' : 'bg-gray-200'
                                                                        } ${isUpdatingRbac || selectedAdminData.role === 'admin' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                    >
                                                                        <span
                                                                            aria-hidden="true"
                                                                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                                                isToggledOn ? 'translate-x-5' : 'translate-x-0'
                                                                            }`}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="py-12 text-center text-gray-500 font-semibold text-sm border border-dashed border-gray-200 rounded-2xl">
                                Select an administrator to manage their custom overrides.
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Create Modal */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                        <div className="p-6 border-b border-gray-100">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-bold text-gray-900">Add New Admin</h3>
                                <button
                                    onClick={() => setIsCreateModalOpen(false)}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleCreateSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Enter admin name"
                                    required
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="Enter email address"
                                    required
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        placeholder="Enter password"
                                        required
                                        className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Mobile Number</label>
                                <input
                                    type="tel"
                                    value={formData.mobile_number}
                                    onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                                    placeholder="Enter mobile number"
                                    required
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                                    id="admin-create-mobile"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Role</label>
                                <select
                                    value={formData.role}
                                    onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                                    required
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white text-sm"
                                    id="admin-create-role"
                                >
                                    <option value="exclusive">Executive</option>
                                    <option value="manager">Manager</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsCreateModalOpen(false)}
                                    className="px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 text-sm font-semibold"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isCreating}
                                    className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 text-sm font-semibold"
                                >
                                    {isCreating ? 'Creating...' : 'Create Admin'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {isEditModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                        <div className="p-6 border-b border-gray-100">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-bold text-gray-900">Edit Admin</h3>
                                <button
                                    onClick={() => setIsEditModalOpen(false)}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Enter admin name"
                                    required
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    disabled
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 text-sm"
                                />
                                <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">New Password</label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        placeholder="Enter new password (leave blank to keep current)"
                                        className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Mobile Number</label>
                                <input
                                    type="tel"
                                    value={formData.mobile_number}
                                    onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                                    placeholder="Enter mobile number"
                                    required
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                                    id="admin-edit-mobile"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center justify-between">
                                    <span>Role</span>
                                    {selectedAdmin?.role === 'admin' && (
                                        <span className="text-[10px] text-blue-600 font-bold uppercase font-mono">Locked</span>
                                    )}
                                </label>
                                <select
                                    value={formData.role}
                                    onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                                    required
                                    disabled={selectedAdmin?.role === 'admin'}
                                    className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white text-sm ${
                                        selectedAdmin?.role === 'admin' ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''
                                    }`}
                                    id="admin-edit-role"
                                >
                                    <option value="exclusive">Executive</option>
                                    <option value="manager">Manager</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsEditModalOpen(false)}
                                    className="px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 text-sm font-semibold"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isUpdating}
                                    className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 text-sm font-semibold"
                                >
                                    {isUpdating ? 'Updating...' : 'Update Admin'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* View Modal */}
            {isViewModalOpen && selectedAdmin && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                        <div className="p-6 border-b border-gray-100">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-bold text-gray-900">Admin Details</h3>
                                <button
                                    onClick={() => setIsViewModalOpen(false)}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="flex items-center space-x-3">
                                <UserAvatar 
                                    src={selectedAdmin.profile_image} 
                                    name={selectedAdmin.name} 
                                    size="md" 
                                />
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">{selectedAdmin.name}</h3>
                                    <p className="text-xs text-gray-400">Admin ID: {selectedAdmin.admin_id}</p>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                                    <Mail className="h-5 w-5 text-gray-400" />
                                    <span className="text-sm font-medium text-gray-800">{selectedAdmin.email}</span>
                                </div>
                                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                                    <Phone className="h-5 w-5 text-gray-400" />
                                    <span className="text-sm font-medium text-gray-800">{selectedAdmin.mobile_number}</span>
                                </div>
                                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                                    <Shield className="h-5 w-5 text-gray-400" />
                                    <span className="text-sm font-medium text-gray-800 capitalize">Role: {selectedAdmin.role || 'exclusive'}</span>
                                </div>
                                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                                    <Calendar className="h-5 w-5 text-gray-400" />
                                    <span className="text-sm font-medium text-gray-800">Created: {formatDate(selectedAdmin.created_at)}</span>
                                </div>
                                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                                    <Activity className="h-5 w-5 text-gray-400" />
                                    <span className="text-sm font-medium text-gray-800">Updated: {formatDate(selectedAdmin.updated_at)}</span>
                                </div>
                            </div>

                            <div className="flex justify-end pt-4">
                                <button
                                    onClick={() => setIsViewModalOpen(false)}
                                    className="px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 text-sm font-semibold"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                        <div className="p-6 border-b border-gray-100">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-bold text-gray-900">Delete Admin</h3>
                                <button
                                    onClick={() => setIsDeleteModalOpen(false)}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="flex items-center space-x-3">
                                <AlertTriangle className="h-8 w-8 text-red-500" />
                                <div>
                                    <p className="text-gray-600 text-sm">
                                        Are you sure you want to delete the admin{' '}
                                        <span className="font-semibold text-gray-900">{selectedAdmin?.name}</span>?
                                    </p>
                                    <p className="text-xs text-gray-400 mt-1">This action cannot be undone.</p>
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    onClick={() => setIsDeleteModalOpen(false)}
                                    className="px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 text-sm font-semibold"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteAdmin}
                                    disabled={isDeleting}
                                    className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 text-sm font-semibold"
                                >
                                    {isDeleting ? 'Deleting...' : 'Delete Admin'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminUsers;